(ns arthur.ui.params
  "The right pane: what the selection is, and what can be changed about it.

  ONLY WHAT CAN BE CHANGED. An id, a span, a count of nodes are either in the
  timeline already or of no use to somebody drawing, so the pane shows controls
  and nothing else, densely: a header naming the selection, then one row per
  thing that can be keyed, then whatever else the selection has. Nothing here
  computes — every control dispatches an intent and every readout comes off a
  subscription."
  (:require [clojure.string :as str]
            [arthur.domain.clip :as clip-domain]
            [arthur.domain.channel :as channel]
            [arthur.domain.feature :as feature]
            [arthur.domain.node :as node]
            [arthur.domain.nest :as nest]
            [arthur.domain.palette :as pal]
            [arthur.domain.paint :as paint]
            [arthur.domain.params :as params]
            [arthur.domain.pose :as pose]
            [arthur.domain.symbol :as symbol]
            [arthur.events.history :as history]
            [arthur.events.playback :as pb]
            [arthur.events.project :as project]
            [arthur.events.ui :as ui]
            [arthur.subs.playback :as playback]
            [arthur.subs.render :as render]
            [arthur.subs.ui :as sub]
            [arthur.ui.icon :as icon]
            [arthur.ui.palette :as palette]
            [arthur.ui.settings :as settings]
            [re-frame.core :as rf]
            [reagent.core :as r]))

(defn- brief
  "A value that fits the column, for the few places an id is still the only name."
  [v]
  (let [s (str v)]
    (if (> (count s) 22) (str (subs s 0 20) "…") s)))

(defn- section [title & body]
  (into [:section.section [:h2 title]] body))

(defn- number-input
  "A number box that edits as it is typed in or stepped, and is one undo step
  from focus to blur. What is typed is kept while it has focus — \"-\" or
  \"4.\" is no number yet, and the value it would round-trip to must not
  replace it under the caret."
  [_]
  (let [draft (r/atom nil)]
    (fn [{:keys [value parse on-number] :as attrs}]
      [:input (merge (dissoc attrs :value :parse :on-number)
                     {:type "number"
                      :value (or @draft value "")
                      :on-focus (fn [e]
                                  (reset! draft (.. e -target -value))
                                  (rf/dispatch [::history/hold]))
                      :on-blur (fn [_]
                                 (reset! draft nil)
                                 (rf/dispatch [::history/settle]))
                      :on-key-down #(when (= "Enter" (.-key %)) (.. % -target blur))
                      :on-change (fn [e]
                                   (let [s (.. e -target -value)
                                         n (parse s)]
                                     (when @draft (reset! draft s))
                                     (when-not (js/isNaN n) (on-number n))))})])))

(defn- count-input
  "A whole number of at least 1, blank meaning \"none of its own\"."
  [value on-change & [{:keys [placeholder disabled label]}]]
  [number-input {:min 1 :step 1 :value value :placeholder placeholder
                 :disabled disabled :aria-label label :title label
                 :parse #(if (seq %) (js/parseInt % 10) nil)
                 :on-number on-change}])

(defn- text-input [value commit! & [attrs]]
  [:input (merge {:type "text" :default-value value
                  :key value
                  :on-key-down #(when (= "Enter" (.-key %)) (.. % -target blur))
                  :on-blur #(commit! (.. % -target -value))}
                 attrs)])

(defn- prop
  "One labelled row of a form: the label in the narrow column, controls after."
  [label & controls]
  (into [:div.prop [:span.prop-name label]] controls))

(defn- palette-select
  "A choice of the project's palettes. `inherit` names the empty choice — what
  an unset palette falls back to — and false leaves it out."
  [value on-change & [disabled? inherit]]
  (let [clip @(rf/subscribe [::render/clip])]
    [:select {:value (str (or value "")) :disabled disabled?
              :on-change (fn [e]
                           (let [v (.. e -target -value)]
                             (on-change (first (filter #(= v (str %)) (keys (pal/palettes clip)))))))}
     (when-not (false? inherit)
       [:option {:value ""} (or inherit "inherit")])
     (for [[id p] (sort-by (comp str :name val) (pal/palettes clip))]
       ^{:key (str id)} [:option {:value (str id)} (:name p)])]))

;; ---------------------------------------------------------------------------
;; the header

(defn- header
  "What is selected, as its name — the one field that says which thing this is.
  An instance is called what it places, so its name IS its symbol's and a rename
  here reaches every placement; `(n)` says how many that is."
  [kind value placeholder commit! uses]
  [:div.inspect-head
   [:span.inspect-kind kind]
   [text-input value commit! {:class "inspect-name" :placeholder placeholder
                              :aria-label "name"}]
   (when (and uses (< 1 uses))
     [:span.uses {:title (str "placed " uses " times · a rename reaches every one")}
      (str "(" uses ")")])])

;; ---------------------------------------------------------------------------
;; one row per channel
;;
;; ◀ ◆ ▶, the name, the value, and the gap: the same row for a transform, a
;; colour, a drawing and a palette, as After Effects puts its key navigator by
;; every property. ◆ keys the channel here or takes the key here off; an edit
;; writes a key on a keyed channel or the one value on one that is not. ◀ ▶ go to
;; the key before and after. The gap switch is the same choice as clicking the
;; gap in the timeline.

(def ^:private order
  (zipmap [[:xform :pos] [:xform :rot] [:xform :scale] [:xform :skew] [:xform :pivot]
           paint/geometry [:style :color] [:vis]]
          (range)))

(defn- gap-at
  "The key whose gap the switch is about, given the keys `ks` and frame `f`: the
  gap the playhead is inside, and standing on a key, the gap ARRIVING at it —
  keying B after A is asking about A→B. On the first key, the gap leaving it."
  [ks f]
  (when f
    (let [left (or (last (filter #(< % f) ks)) (when (= f (first ks)) f))]
      (when (and left (some #(< left %) ks)) left))))

(defn- seek-own!
  "Put the playhead on own frame `f` of something whose clock is `time`."
  [time f]
  (rf/dispatch [::pb/seek (js/Math.round (+ (:at time) (/ f (:rate time))))]))

(defn- key-nav
  "◀ ◆ ▶ over keys `ks`: `frame` is the one being edited, nil off screen, and
  `own` is `{:time :frame}`, the clock the jumps go through."
  [ks frame {:keys [time] here :frame} on-key title]
  (let [ks   (sort ks)
        here (or frame here)
        before (when here (last (filter #(< % here) ks)))
        after  (when here (first (filter #(> % here) ks)))]
    [:span.key-nav
     [:button.nav {:disabled (or (nil? before) (nil? time))
                   :title (if before (str "key at " before) "no key before")
                   :aria-label "previous key"
                   :on-click #(seek-own! time before)}
      [icon/view :prev]]
     [:button.key {:class (cond (some #{frame} ks) "on" (seq ks) "keyed")
                   :disabled (nil? frame)
                   :title title
                   :on-click on-key}
      "◆"]
     [:button.nav {:disabled (or (nil? after) (nil? time))
                   :title (if after (str "key at " after) "no key after")
                   :aria-label "next key"
                   :on-click #(seek-own! time after)}
      [icon/view :next]]]))

(defn- gap-switch [ch path frame set-gap!]
  (let [ks   (sort (keys (:keys ch)))
        ;; Only a channel with a gap between keys is read; a measured one has
        ;; no keys, and its value lives in a store the inspector doesn't hold.
        left (when-let [l (gap-at ks frame)]
               (when-not (node/hold-only? path (channel/value-at ch frame nil))
                 l))
        tween? (and left (= :linear (channel/segment-interp ch left)))]
    (if left
      [:button.gap {:class (when tween? "on")
                    :title (str (if tween? "tween" "hold") " from key " left
                                " · click to " (if tween? "hold" "tween"))
                    :aria-label (if tween? "tween" "hold")
                    :on-click #(set-gap! left (if tween? :hold :linear))}
       [icon/view (if tween? :tween :hold)]]
      [:span.gap])))

(defn- channel-row
  "Channel `path`, `ch`, of whatever the row is about. `frame` is its own frame
  being edited, nil where it is not on screen and a keyed channel has no here to
  write to; `own` is its clock. `on-key` and `set-gap!` are the writes, `value`
  the editor."
  [{:keys [path ch frame own live? on-key set-gap!]} value]
  (let [ks (keys (:keys ch))]
    [:div.chan {:class (when live? "live") :data-channel (str/join " " (map name path))}
     [key-nav ks frame own on-key
      (if (seq ks) (str (count ks) " keys · key or unkey here") "key this here")]
     [:span.chan-name (node/channel-name path)]
     [:span.chan-value value]
     [gap-switch ch path frame set-gap!]]))

(defn- number-fields
  "The value of a number or a vector of them, as boxes. Rotation is shown in
  degrees."
  [path v off? put]
  (let [deg? (= path [:xform :rot])
        field (fn [i x on-number]
                ^{:key i}
                [number-input {:step "any" :disabled off?
                               :value (if deg? (/ (js/Math.round (* x (/ 18000 js/Math.PI))) 100) x)
                               :parse js/parseFloat
                               :on-number #(on-number (if deg? (* % (/ js/Math.PI 180)) %))}])]
    (if (number? v)
      (field 0 v put)
      (doall (map-indexed (fn [i x] (field i x #(put (assoc (vec v) i %)))) v)))))

(defn- swatches
  "A shape's colour, to change: every slot, then clear and remap, as the strip
  beside the tools offers them for the next shape. This is where something
  already drawn is recoloured — the strip only says what is drawn next."
  [v off? palette pick!]
  ;; A slot by name is the slot of that name.
  (let [value (cond (integer? v) v
                    (keyword? v) (first (keep-indexed #(when (= v (:name %2)) %1) (:slots palette))))]
    [:span.chan-swatches
     (doall
      (for [[i {:keys [name hex]}] (map-indexed vector (:slots palette))]
        ^{:key i}
        [:button.swatch
         {:class (when (= i value) "on")
          :style {:background hex}
          :disabled off?
          :aria-label (str "color " i (when name (str " " (clojure.core/name name))))
          :aria-pressed (= i value)
          :title (str i (when name (str " · " (clojure.core/name name))) " · " hex)
          :on-click #(pick! i)}]))
     [:button.swatch.clear
      {:class (when (= :clear v) "on")
       :disabled off?
       :aria-label "clear" :aria-pressed (= :clear v)
       :title "clear — a knockout: a hole in its symbol"
       :on-click #(pick! :clear)}]
     [:button.swatch.map
      {:class (when (symbol/remap? v) "on")
       :disabled off?
       :aria-label "remap" :aria-pressed (boolean (symbol/remap? v))
       :title "remap — lights what is under it in other slots; set which below"
       :on-click #(when-not (symbol/remap? v) (pick! [:remap {}]))}]]))

(defn- channel-value
  "The editor for channel `path` of node `n`: boxes, a switch, swatches — or,
  where there is nothing to type, what it is."
  [sid id n path ch frame palette node-path]
  (let [keyed? (some? (:keys ch))
        off?   (and keyed? (nil? frame))
        v      (when-not (:dense ch) (channel/value-at ch (or frame 0) nil))
        put    #(rf/dispatch [::project/set-channel sid id path frame %])]
    (cond
      (:dense ch)
      [:span.dim {:title "measured from the footage · regenerate to change it"}
       (str "measured · " (get-in ch [:dense :frames]) "f")]

      (= path paint/geometry)
      [:span.dim {:title "double-click the shape on the stage to edit its points"}
       (str (quot (count v) 2) " points")]

      (= path [:style :color])
      (swatches v off? palette put)

      (boolean? v)
      [:input {:type "checkbox" :checked v :disabled off?
               :on-change #(put (.. % -target -checked))}]

      (or (number? v) (and (sequential? v) (every? number? v)))
      [:<>
       (number-fields path v off? put)
       ;; A pivot does not follow the drawing, so "put it back in the middle of
       ;; what is there now" has to be something you can ask for. Moves nothing.
       (when (and (= path [:xform :pivot]) (seq node-path))
         [:button.icon {:title "centre the pivot on what it draws now"
                        :aria-label "centre pivot"
                        :on-click #(rf/dispatch [::ui/centre-pivot node-path])}
          "⌖"])]

      :else [:span.dim (brief (pr-str v))])))

(defn- channels
  "Every channel of node `n`, transform first."
  [[sid id n] node-path]
  (let [auto-key? @(rf/subscribe [::sub/auto-key?])
        clip      @(rf/subscribe [::render/clip])
        frame     (:frame @(rf/subscribe [::sub/selected-placement]))
        own       @(rf/subscribe [::sub/own-time node-path])
        ;; What the shape is drawn in, which is what the grid follows.
        palette   (get (pal/palettes clip) @(rf/subscribe [:arthur.ui.palette/followed]))]
    [:div.chans
     (doall
      (for [[path ch] (sort-by (fn [[path]] [(get order path 99) (str path)]) (node/channels n))]
        ^{:key (str path)}
        [channel-row {:path path :ch ch :frame frame :own own
                      :live? (and auto-key? (not (:dense ch)))
                      :on-key #(rf/dispatch [::project/toggle-key sid id path frame])
                      :set-gap! #(rf/dispatch [::project/set-segment-interp sid id path %1 %2])}
         [channel-value sid id n path ch frame palette node-path]]))]))

;; ---------------------------------------------------------------------------
;; an instance's playback, and a symbol's own settings

(def ^:private modes
  [[:once :once "play once"] [:loop :loop "loop"] [:frame :still "hold one frame"]])

(defn- playback-row [[sid id n]]
  (let [{:keys [in speed end]} (node/playback-of n)
        mode (cond (zero? speed) :frame
                   (or (= :loop end) (get-in n [:time :loop?])) :loop
                   :else :once)
        change! #(rf/dispatch [::project/instance-playback sid id %1 %2])]
    (prop "play"
          [:span.seg
           (doall
            (for [[m glyph title] modes]
              ^{:key m}
              [:button {:class (when (= m mode) "on") :title title :aria-label title
                        :aria-pressed (= m mode)
                        :on-click #(change! :mode m)}
               [icon/view glyph]]))]
          [:span.dim "from"]
          [number-input {:min 0 :step 1 :value in
                         :title "the frame of its symbol it starts on"
                         :aria-label "instance start frame"
                         :parse #(js/parseInt % 10)
                         :on-number #(change! :in %)}]
          [:span.dim "×"]
          [number-input {:min 0 :step 0.1 :value speed
                         :disabled (= :frame mode)
                         :title "speed" :aria-label "instance playback speed"
                         :parse js/parseFloat
                         :on-number #(change! :speed %)}])))

(defn- symbol-fields
  "A symbol's length, stage and authoring palette."
  [sid]
  (let [clip  @(rf/subscribe [::render/clip])
        sym   (get-in clip [:symbols sid])
        busy? (:busy? @(rf/subscribe [::playback/project]))
        put!  #(rf/dispatch [::project/symbol-setting sid %1 %2])]
    [:<>
     (prop "length"
           [count-input (:frames sym) #(put! :frames %) {:disabled busy? :label "length in frames"}]
           [:span.dim "f"])
     (prop "size"
           [count-input (:width sym) #(put! :width %)
            {:placeholder (str (:width clip)) :disabled busy? :label "width"}]
           [:span.dim "×"]
           [count-input (:height sym) #(put! :height %)
            {:placeholder (str (:height clip)) :disabled busy? :label "height"}]
           (when (or (:width sym) (:height sym))
             [:button.icon {:disabled busy? :title "use the project's stage size"
                            :aria-label "use project stage"
                            :on-click #(do (put! :width nil) (put! :height nil))}
              "↺"]))
     (prop "palette"
           [palette-select (:palette sym) #(rf/dispatch [::project/symbol-palette sid %]) busy?
            ;; Unset, a symbol draws in what it is placed in, and on its own in
            ;; the project's palette.
            (if (pos? (clip-domain/uses clip sid))
              "from where it's placed"
              (str "project's · " (:name (get (pal/palettes clip) (pal/default-palette-id clip)))))])]))

(defn- project-fields []
  (let [clip    @(rf/subscribe [::render/clip])
        project @(rf/subscribe [::playback/project])
        busy?   (:busy? project)]
    [section "project"
     (prop "name" [text-input (or (:name project) (:name clip) "")
                   #(rf/dispatch [::project/rename %]) {:aria-label "project name"}])
     (prop "stage"
           [count-input (:width clip) #(rf/dispatch [::project/project-setting :width %])
            {:disabled busy? :label "width"}]
           [:span.dim "×"]
           [count-input (:height clip) #(rf/dispatch [::project/project-setting :height %])
            {:disabled busy? :label "height"}])
     (prop "rate" [settings/rate busy?])
     (prop "palette"
           [palette-select (pal/default-palette-id clip)
            #(when % (rf/dispatch [::project/default-palette %])) busy? false])]))

;; ---------------------------------------------------------------------------
;; a node

(defn- node-view
  "A selected node: its name, its channels, and for an instance how it plays
  and the settings of the symbol it places."
  [[sid id n :as selected]]
  (let [clip      @(rf/subscribe [::render/clip])
        ;; The row path, which names the node the way a stage gesture does: its
        ;; clock and its pivot are found where it is placed.
        node-path (or (nth @(rf/subscribe [::sub/selection]) 3 nil) [id])
        of        (node/source n)]
    [:<>
     (if of
       [header (if (symbol/lane? (clip-domain/symbol clip of)) "lane" "instance")
        (clip-domain/symbol-name clip of) (name of)
        #(rf/dispatch [::ui/rename-node sid id %]) (clip-domain/uses clip of)]
       [header (name (:kind n)) (or (:name n) "") (clip-domain/node-label clip id n)
        #(rf/dispatch [::ui/rename-node sid id %]) nil])
     [channels selected node-path]
     (when of
       [:div.props
        [playback-row selected]
        [symbol-fields of]])]))

(defn- palette-placement-view
  "A placement on the palette lane: which palette it switches to, keyed and
  tweened like any channel."
  [[sid id n]]
  (let [clip @(rf/subscribe [::render/clip])
        transition (node/source n)
        open-frame @(rf/subscribe [::render/open-frame])
        [start _] (node/placed-span n)
        frame (max 0 (- open-frame (or start 0)))
        own {:time {:at (or start 0) :rate 1} :frame frame}
        ch (or (get-in n [:channels [:palette]])
               (assoc (channel/framed
                       (get-in clip [:symbols transition :palette-ref]))
                      :semantic :palette))
        value (channel/value-at ch frame nil)
        chosen (if (map? value) (:from value) value)]
    [:<>
     [header "palette" (clip-domain/symbol-name clip transition) (name transition)
      #(rf/dispatch [::ui/rename-node sid id %]) nil]
     [:div.chans
      [channel-row {:path [:palette] :ch ch :frame frame :own own
                    :on-key #(rf/dispatch [::project/toggle-palette-key sid id frame])
                    :set-gap! #(rf/dispatch [::project/set-segment-interp sid id [:palette] %1 %2])}
       [palette-select (when-not (= pal/inherit chosen) chosen)
        #(rf/dispatch [::project/set-palette-choice sid id frame (or % pal/inherit)])]]]]))
;; ---------------------------------------------------------------------------
;; corrections

(defn- correction-initial [clip sid id]
  (let [n (get-in clip [:symbols sid :nodes id])
        [a b] (or (:span n) [0 3])
        a (if (integer? a) a 0)
        through (max a (min (dec (if (integer? b) b 3)) (+ a 2)))]
    {:target id :path [:xform :rot] :motion :constant
     :from a :through through :peak-frame (min (dec through) (inc a))
     :delta 0 :delta-x 0 :delta-y 0
     :start 0 :start-x 0 :start-y 0
     :end 0 :end-x 0 :end-y 0
     :peak 0 :peak-x 0 :peak-y 0}))

(defn- draft-number [draft key label integer?]
  [:label.inspector-field label
   [:input {:type "number" :step (if integer? 1 "any")
            :value (or (get @draft key) "")
            :on-change (fn [e]
                         (let [s (.. e -target -value)
                               n ((if integer? js/parseInt js/parseFloat) s 10)]
                           (swap! draft assoc key (when-not (js/isNaN n) n))))}]])

(defn- value-inputs [draft prefix label]
  (if (= [:xform :rot] (:path @draft))
    [draft-number draft prefix (str label " (degrees)") false]
    [:<>
     [draft-number draft (keyword (str (name prefix) "-x")) (str label " x") false]
     [draft-number draft (keyword (str (name prefix) "-y")) (str label " y") false]]))

(defn- correction-value [d prefix]
  (if (= [:xform :rot] (:path d))
    (some-> (get d prefix) (* (/ js/Math.PI 180)))
    [(get d (keyword (str (name prefix) "-x")))
     (get d (keyword (str (name prefix) "-y")))]))

(defn- correction-spec [d]
  (let [base {:support [(:from d) (when (number? (:through d)) (inc (:through d)))]
              :motion (:motion d)}]
    (case (:motion d)
      :constant (assoc base :delta (correction-value d :delta))
      :ramp (assoc base :start (correction-value d :start)
                         :end (correction-value d :end))
      :return (assoc base :start (correction-value d :start)
                           :peak (correction-value d :peak)
                           :peak-frame (:peak-frame d))
      base)))

(defn- correction-layers [clip sid id]
  (for [[path c] (get-in clip [:symbols sid :nodes id :channels])
        l (:over c)]
    {:path path :layer l}))

(defn- correction-section [[sid selected-id selected]]
  (let [clip @(rf/subscribe [::render/clip])
        lane-context? (or (symbol/lane? (clip-domain/symbol clip sid))
                          (and (= :instance (:kind selected))
                               (symbol/lane? (clip-domain/symbol clip (node/source selected)))))
]
    (when lane-context?
      (r/with-let [draft (r/atom (correction-initial clip sid selected-id))]
        (let [target (:target @draft)
              motion (:motion @draft)
              conflicts (clip-domain/conflicts clip)
              layers (correction-layers clip sid target)]
          [section "corrections"
           [:div.correction-grid
            [:label.inspector-field "property"
             [:select {:value (if (= [:xform :rot] (:path @draft)) "rotation" "position")
                       :on-change #(swap! draft assoc :path
                                         (if (= "rotation" (.. % -target -value))
                                           [:xform :rot] [:xform :pos]))}
              [:option {:value "rotation"} "rotation offset"]
              [:option {:value "position"} "position offset"]]]
            [:label.inspector-field "motion"
             [:select {:value (name motion)
                       :on-change #(swap! draft assoc :motion (keyword (.. % -target -value)))}
              [:option {:value "constant"} "constant"]
              [:option {:value "ramp"} "ramp"]
              [:option {:value "return"} "return"]]]
            [draft-number draft :from "from owner frame" true]
            [draft-number draft :through "through owner frame" true]
            (case motion
              :constant [value-inputs draft :delta "offset"]
              :ramp [:<> [value-inputs draft :start "start offset"]
                          [value-inputs draft :end "end offset"]]
              :return [:<> [value-inputs draft :start "start offset"]
                            [value-inputs draft :peak "peak offset"]
                            [draft-number draft :peak-frame "peak owner frame" true]]
              nil)]
           [:div.row {:style {:margin-top "6px"}}
            [:button {:on-click #(rf/dispatch [::ui/add-correction sid target
                                               (:path @draft) (correction-spec @draft)])}
             "apply correction"]]
           (when (seq layers)
             [:div.correction-list
              (doall
               (for [{:keys [path layer]} layers]
                 ^{:key (str path (:id layer))}
                 [:div.correction-item
                  [:span {:title (pr-str (:id layer))}
                   (str (str/join " " (map name path)) " · "
                        (pr-str (:support layer))
                        (when (:conflict layer) " · conflict"))]
                  (when (:conflict layer)
                    [:button {:on-click #(rf/dispatch [::ui/retry-correction
                                                       sid target path (:id layer)])}
                     "retry"])
                  [:button {:on-click #(rf/dispatch [::ui/remove-correction
                                                     sid target path (:id layer)])}
                   "remove"]]))])
           (when (seq conflicts)
             [:div.correction-conflicts
              [:div.dim "document conflicts"]
              (doall
               (for [{:keys [symbol node channel id why]} conflicts]
                 ^{:key (str symbol node channel id)}
                 [:div {:title why} (str (brief node) " · "
                                         (str/join " " (map name channel)) " · " why)]))])])))))

;; ---------------------------------------------------------------------------
;; tracing layers
;;
;; A tracing layer is a placement of a tracing symbol, and a face's footage is
;; one of them — its `:plate`, under its head — so one section serves both:
;; whether it is showing, and which of its frames it holds on. Showing is the
;; editor's (`arthur.db/tracing`); the holds are the document's, on the
;; placement's own `:time`. A face adds one fact of its own below them: whether
;; its head follows those holds.

(defn- layer-section
  "Tracing layer `id` of symbol `sid`, at row `path` from the open symbol, under
  `title`, with `more` appended. Its holds are keys like any other: ◆ holds the
  picture here until the next one, ◀ ▶ go between them."
  [title sid id path & more]
  (let [n     (get-in @(rf/subscribe [::render/clip]) [:symbols sid :nodes id])
        {:keys [on? hidden]} @(rf/subscribe [::render/tracing])
        shown? (and on? (not (contains? hidden [sid id])))
        {:keys [frame] :as own} @(rf/subscribe [::sub/own-time path])
        holds (get-in n [:time :holds] [])]
    (into
     [section title
      [:div.chan
       [key-nav holds frame own #(rf/dispatch [::project/toggle-hold sid id frame])
        (if (nil? frame)
          "move the playhead over this layer to hold a frame"
          "hold the picture on this frame until the next hold")]
       [:span.chan-name "hold"]
       [:span.chan-value
        [:label.dim {:title "show it over the picture · a reference, never exported"}
         [:input {:type "checkbox" :checked (boolean shown?)
                  :on-change #(rf/dispatch [::ui/show-trace [sid id] (not shown?)])}]
         " show"]]
       [:span.gap]]]
     more)))

(def ^:private origins
  "How a face's head moves between its footage's holds, as the head's `:reads`."
  [["continuous" nil                  "the head reads the frame it is on"]
   ["at holds"   {:holds-of :plate}   "the head jumps to each hold and stays there"]
   ["start"      {:holds [0]}         "the head holds frame 0 forever"]])

(defn- face-section
  "Face `face`'s footage, at row `path` from the open symbol: its plate as a
  tracing layer, and how its head follows the plate's holds."
  [face path]
  (let [reads (get-in @(rf/subscribe [::render/clip]) [:symbols face :nodes :head :reads])]
    [layer-section (str "tracing · " (name face)) face :plate (conj path :plate)
     [:div.prop
      [:span.prop-name "head"]
      (doall
       (for [[label r title] origins]
         ^{:key label}
         [:button {:class (when (= r reads) "on") :title title
                   :on-click #(rf/dispatch [::project/set-reads face r])}
          label]))]]))

(defn- performance-section
  "Smart frame picking for one face, and what it has to work with.

  ONE SWITCH AND NO TOLERANCE. The output rate or the exposure setting has already
  chosen how sparse the performance is; all the snap decides is WHICH native frame
  an already-decided slot reads, so there is nothing here for a knob to mean. See
  docs/frame-selection.md.

  The groups and their counts stand in for the frame strip that document specifies,
  which is not built. What a person needs before switching this on is whether the
  take has any closures stored at all: a take whose mouth never shuts far enough to
  cross `aperture-cut` is marked nowhere, and the switch would otherwise read as
  broken rather than as having nothing to do.

  EDITOR STATE, and the readout says so. The document setting wants a leaf and a
  round trip of its own; until then this moves the stage and not an export."
  [face]
  (let [clip  @(rf/subscribe [::render/clip])
        store @(rf/subscribe [::render/store])
        smart @(rf/subscribe [::render/smart])
        sym   (get-in clip [:symbols face])
        marks (pose/marks (:nodes sym) (:frames sym) store)
        on?   (contains? smart face)]
    [section (str "performance \u00b7 " (name face))
     [:div.row
      [:label {:title (str "let a closure the grid drops move the frame a slot "
                           "reads \u2014 backward, and only within that slot's own gap"
                           " \u00b7 the stage only: an export is unaffected until this is a saved setting")}
       [:input {:type "checkbox" :checked on?
                :on-change #(rf/dispatch [::ui/smart-picking face (not on?)])}]
       " smart frame picking"]]
     (if (empty? marks)
       [:div.row [:span.dim "no closures stored on this face \u2014 nothing to land on"]]
       [:div.row
        [:span.dim "marks"]
        (doall
         (for [[group fs] (sort-by (comp str key) marks)]
           ^{:key (str group)}
           [:span.dim {:style {:margin-right "8px"}
                       :title (str "frames the stored cut calls shut: "
                                   (str/join " " (take 24 fs))
                                   (when (> (count fs) 24) " \u2026"))}
            (str (if (vector? group) (str/join "/" (map name group)) (name group))
                 " " (count fs))]))])]))

;; ---------------------------------------------------------------------------
;; tracked objects
;;
;; The generated settings. Unlike everything above, moving one of these does real
;; work: `::project/preview-settings` re-freezes whatever blocks the knob
;; invalidates, which is why the slider's value is held in `:ui :knobs` until the
;; regeneration comes back.

(defn- owners [clip]
  (vec (for [[scope objects] [[:subject (:subjects clip)]
                              [:feature (:features clip)]
                              [:group (:groups clip)]]
             id (sort-by str (keys objects))]
         [scope id])))

(defn- bounds [knob {:keys [type default min even?] upper :max}]
  {:min  (or min (if (= knob :blob-grow) -3 0))
   :max  (or upper (get {:verts 20 :eye-verts 16 :brow-verts 10
                         :teeth-verts 20 :blob-grow 3 :top-bias 2
                         :gaze-gain 4} knob)
             (max 1 (* 2 default)))
   :step (cond even? 2 (= type :integer) 1 :else 0.01)})

(defn- subject-of-owner [clip [scope id]]
  (case scope
    :subject id
    :feature (get-in clip [:features id :subject])
    :group   (get-in clip [:groups id :subject])
    nil))

(defn- face?
  "Is symbol `sid` a face: does it have a measured head?"
  [clip sid]
  (boolean (seq (get-in clip [:symbols sid :nodes :head :measured]))))

(defn- face-selection
  "The one face explicitly named by a face symbol, its placement, or a tracking
  owner.  A shape merely living inside an open face is deliberately not one."
  [clip selection selected-node]
  (let [[kind id] selection
        placed (node/source (peek selected-node))
        candidate (cond
                    (= :symbol kind) id
                    (#{:subject :feature :group} kind) (subject-of-owner clip selection)
                    (= :node kind) placed)]
    (when (and candidate (face? clip candidate)) candidate)))

(defn- footage-faces [clip selection selected-node]
  (if (= :footage (first selection))
    (let [footage-id (second selection)]
      (into [] (comp (filter #(= footage-id (:footage (val %)))) (map key))
            (:subjects clip)))
    (some-> (face-selection clip selection selected-node) vector)))

(defn- tracking-owners [clip selection selected-node]
  (let [faces (if (= :footage (first selection))
                (set (footage-faces clip selection selected-node))
                (some-> (face-selection clip selection selected-node) hash-set))]
    (when (seq faces)
      (filterv #(contains? faces (subject-of-owner clip %)) (owners clip)))))

(defn- lead-row
  "How far feature `fid`'s drawing reads ahead of the sound, as its first node's
  `:time :offset`. Stored in the face's own camera frames and typed in the
  document's, because the document's grid is what a lead is felt against."
  [clip fid]
  (let [{face :symbol [id] :nodes} (get-in clip [:features fid])
        face-fps (get-in clip [:analyses (get-in clip [:subjects face :analysis]) :fps])
        fps      (:fps clip)
        offset   (get-in clip [:symbols face :nodes id :time :offset] 0)]
    (when (and face-fps fps)
      [:div.prop {:style {:margin-top "6px"}
                  :title "roto drawn a frame or two ahead of the sound reads as in sync"}
       [:span.prop-name {:style {:flex "none"}} "mouth lead"]
       [number-input {:step 0.5 :aria-label "lead in frames"
                      :value (/ (js/Math.round (* 10 offset (/ fps face-fps))) 10)
                      :parse js/parseFloat
                      :on-number #(rf/dispatch [::project/set-lead face id
                                                (js/Math.round (* % (/ face-fps fps)))])}]
       [:span.dim (str "f · " (js/Math.round (/ (* 1000 offset) face-fps)) "ms")]])))

(defn- tracking-section
  "The generated settings of one of `all`, the tracked objects in view.

  WHICH ONE IS SHOWN IS NOT THE SELECTION. The dropdown used to select the
  object it picked, which deselected the placement every other section of the
  inspector is about, so the inspector emptied under the person using it. The
  pick is kept here instead, `[selection owner]`, and only for as long as the
  selection it was made under: select something else and the section follows."
  [_]
  (let [shown (r/atom nil)]
    (fn [all]
      (let [clip      @(rf/subscribe [::render/clip])
            selection @(rf/subscribe [::sub/selection])
            knobs     @(rf/subscribe [::sub/knobs])
            busy?     (:busy? @(rf/subscribe [::playback/project]))
            report    @(rf/subscribe [::project/regeneration])
            [under picked] @shown
            [scope id :as owner] (cond
                                   (and (= under selection) (some #{picked} all)) picked
                                   (some #{selection} all) selection
                                   :else (first all))
            ;; The face's mouth, whose lead is shown with its face and with itself.
            mouth (case scope
                    :subject (let [m (feature/owned id :mouth)]
                               (when (get-in clip [:features m]) m))
                    :feature (when (= :mouth (get-in clip [:features id :area])) id)
                    nil)
            area (case scope
                   :subject :subject
                   :feature (get-in clip [:features id :area])
                   :group   :eye
                   nil)
            values (case scope
                     :subject (merge (params/for-area :subject)
                                     (get-in clip [:subjects id :params]))
                     :feature (when id (feature/effective-params clip id))
                     :group   (merge (params/for-area :eye)
                                     (get-in clip [:groups id :params]))
                     nil)]
        [section "tracking"
         ;; The option VALUE is the INDEX, not the owner. A `[scope id]` pair written
         ;; into the DOM comes back as a string that has to be parsed back into a
         ;; keyword pair, and `subs`/`keyword` on a namespaced id loses its namespace
         ;; — the same trap `events/export/target-value` exists to avoid. An index
         ;; into a vector both ends agree on cannot be misread.
         [:select {:value (or (first (keep-indexed #(when (= %2 owner) %1) all)) 0)
                   :on-change #(reset! shown [selection (nth all (js/parseInt (.. % -target -value) 10))])}
          (doall
           (for [[i [kind object-id]] (map-indexed vector all)]
             ^{:key i}
             [:option {:value i} (str (name kind) " · " (subs (str object-id) 1))]))]
         (when mouth [lead-row clip mouth])
         (when area
           [:div {:style {:margin-top "6px"}}
            (doall
             (for [[knob spec] (sort-by (comp str key) params/definitions)
                   :when (= area (:area spec))]
               (let [k (get knobs [scope id knob] (get values knob))
                     {:keys [min max step]} (bounds knob spec)]
                 ^{:key knob}
                 [:label.knob
                  [:span.top-line [:span.name (name knob)] [:span (str k)]]
                  [:input {:type "range" :min min :max max :step step :value k
                           :disabled busy?
                           :on-change
                           (fn [^js event]
                             (let [s (.. event -target -value)
                                   v (if (= :integer (:type spec))
                                       (js/parseInt s 10)
                                       (js/parseFloat s))]
                               (rf/dispatch [::ui/set-knob scope id knob v])
                               (rf/dispatch [::project/preview-settings
                                             {:scope scope :id id :knob knob :value v}])))}]])))])
         (when report
           ;; `regeneration-debug` is a hook rather than a style: `test/browser` reads
           ;; this line to assert which feature a knob dirtied and which tier it
           ;; reached, and that is the one thing on the page that says so.
           [:div.dim.regeneration-debug {:style {:margin-top "6px"}}
            (str "dirty: " (pr-str (:features report))
                 " · blocks: " (if (seq (:roles report))
                                 (pr-str (sort (:roles report))) "tier 1 only"))])]))))

;; ---------------------------------------------------------------------------

(defn- eye-repair-controls [sid {:keys [id from through]} current]
  (let [clip @(rf/subscribe [::render/clip])
        in-range? (and (integer? current) (<= from current through))]
    (r/with-let [draft (r/atom {:side :l :opening 1 :gaze-x 0 :gaze-y 0})]
      (let [side (:side @draft)
            layer-id (str id "/eye/" (name side))
            eye (keyword (str "eye-" (name side)))
            iris (keyword (str "iris-" (name side)))
            find-layer (fn [node path]
                         (some #(when (= layer-id (:id %)) %)
                               (get-in clip [:symbols sid :nodes node :channels path :over])))
            lids (find-layer eye [:geom :pts])
            gaze (find-layer iris [:xform :pos])
            load! (fn []
                    (when in-range?
                      (let [opening (if lids (channel/value-at (:values lids) current nil) 1)
                            offset (if gaze (channel/value-at (:values gaze) current nil) [0 0])]
                        (swap! draft assoc :opening opening
                               :gaze-x (* 100 (first offset)) :gaze-y (* 100 (second offset))))))]
        [:div {:style {:margin-top "8px"}
               :title "eye adjustments · key a pose, then scrub and key another; they tween within this interval"}
         [:label.inspector-field "eye"
          [:select {:value (name side)
                    :on-change #(swap! draft assoc :side (keyword (.. % -target -value)))}
           [:option {:value "l"} "left eye"]
           [:option {:value "r"} "right eye"]]]
         [draft-number draft :opening "lid opening (1 = donor, 0 = closed)" false]
         [draft-number draft :gaze-x "gaze x (% of image height)" false]
         [draft-number draft :gaze-y "gaze y (% of image height)" false]
         [:div.row
          [:button {:disabled (not in-range?) :on-click load!} "load current adjustment"]
          [:button {:disabled (not in-range?)
                    :on-click #(rf/dispatch
                                [::ui/eye-key sid id side current
                                 {:opening (:opening @draft)
                                  :gaze-x (when (number? (:gaze-x @draft)) (/ (:gaze-x @draft) 100))
                                  :gaze-y (when (number? (:gaze-y @draft)) (/ (:gaze-y @draft) 100))}])}
           (if in-range? (str "key eye at frame " current) "scrub inside interval to key eye")]]
         (when lids
           [:div.row
            [:span.dim (str "eye keys: " (str/join ", " (sort (keys (get-in lids [:values :keys])))))]
            [:button {:on-click #(rf/dispatch [::ui/remove-eye-keys sid id side])} "reset eye adjustments"]])]))))

(defn- repair-section [sid path]
  (let [status (:status @(rf/subscribe [::playback/project]))
        clip @(rf/subscribe [::render/clip])
        store @(rf/subscribe [::render/store])
        open @(rf/subscribe [::render/open])
        frame @(rf/subscribe [::render/open-frame])
        inside (nest/inside clip store open path frame)
        current (when (= sid (:sid inside)) (:frame inside))
        usable? (and (integer? current)
                     (<= 0 current (dec (get-in clip [:symbols sid :frames]))))
        repairs (distinct (for [[_ n] (get-in clip [:symbols sid :nodes])
                                [_ c] (:channels n) r (:repairs c)] r))]
    (r/with-let [draft (r/atom {:from 0 :through 1 :donor 2 :head? false})]
      [section [:span {:title "hold a clean pose across damaged frames · face settings still apply · frames are this face's own, from 0"}
                "repair intervals"]
       (for [[field label] [[:from "from face frame"]
                            [:through "through face frame"]
                            [:donor "clean donor frame"]]]
         ^{:key field}
         [:div.row
          [draft-number draft field label true]
          [:button {:disabled (not usable?)
                    :title (if usable? (str "use face frame " current)
                               "move the playhead over this face")
                    :on-click #(swap! draft assoc field current)}
           "use current frame"]])
       [:div.dim (if usable? (str "current face frame: " current)
                     "Move the playhead over this face to pick its current frame.")]
       [:label [:input {:type "checkbox" :checked (:head? @draft)
                        :on-change #(swap! draft assoc :head? (.. % -target -checked))}]
        " borrow head movement too"]
       [:button {:on-click #(rf/dispatch [::ui/borrow-pose sid @draft])} "borrow pose"]
       (when status [:div.dim {:role "status"} status])
       (for [{:keys [id from through donor] :as repair} repairs]
         ^{:key (str id)}
         [:div {:style {:margin-top "10px"}}
          [:div.row
           [:span (str from "–" through " ← frame " donor)]
           [:button {:on-click #(rf/dispatch [::ui/remove-repair sid id])} "remove"]]
          [eye-repair-controls sid repair current]])])))

;; ---------------------------------------------------------------------------

(defn- symbol-view
  "A symbol, selected or open with nothing selected: its name and settings."
  [sid]
  (let [clip @(rf/subscribe [::render/clip])
        sym  (get-in clip [:symbols sid])]
    [:<>
     [header (if (= :palette (:type sym)) "palette" "symbol")
      (clip-domain/symbol-name clip sid) (name sid)
      #(rf/dispatch [::project/rename-symbol sid %]) (clip-domain/uses clip sid)]
     [:div.props
      (if (= :palette (:type sym))
        (prop "palette"
              [:span {:title "keys and tweens are set on a placement in the palette lane"}
               (or (get-in clip [:palettes (:palette-ref sym) :name]) (str (:palette-ref sym)))])
        [symbol-fields sid])]]))

(defn view []
  (let [clip      @(rf/subscribe [::render/clip])
        open      @(rf/subscribe [::render/open])
        selection @(rf/subscribe [::sub/selection])
        ;; Nothing selected inspects the symbol that is open, as clicking its
        ;; tab would.
        inspected (or selection [:symbol open])
        node      @(rf/subscribe [::sub/selected-node])
        ;; The symbol the selected node places, or nil.
        placed    (node/source (peek node))
        tracking-owners (tracking-owners clip selection node)
        palette-placement? (and node
                                (= :palette-track
                                   (get-in clip [:symbols (first node) :type])))
        palette-symbol? (and (= :symbol (first inspected))
                             (= :palette (get-in clip [:symbols (second inspected) :type])))
        ;; The face the face sections are about: the selected placement's symbol,
        ;; or the open symbol when that is a face being drawn over.
        face      (or placed (when (face? clip open) open))
        layer?    (and placed (clip-domain/trace? (clip-domain/symbol clip placed)))
        ;; Where that face or layer sits, as a row path from the open symbol. A
        ;; selection made on the stage has no path and names a node directly in
        ;; the open symbol; the open symbol itself is at no path at all.
        path      (if placed (or (nth selection 3 nil) [(second node)]) [])
        a-face?   (and face (face? clip face) (not layer?))]
    [:section.pane.params {:aria-label "inspector"}
     [:div.pane-head "inspector"]
     [:div.inspect
      (cond
        palette-placement?           [palette-placement-view node]
        node                         [node-view node]
        (= :symbol (first inspected)) [symbol-view (second inspected)])
      (when (and node (not palette-placement?)) [palette/remap-section])
      (when (and node (not palette-placement?)) ^{:key (str (first node) "/" (second node))}
        [correction-section node])
      (when layer?
        [layer-section "tracing" (first node) (second node) path])
      (when (get-in clip [:symbols face :nodes :plate])
        [face-section face path])
      (when (and (seq tracking-owners) (not palette-placement?) (not palette-symbol?))
        [tracking-section tracking-owners])
      (when a-face? ^{:key (str "repair/" face)} [repair-section face path])
      (when a-face? ^{:key (str "perf/" face)} [performance-section face])
      (when (= [:symbol (clip-domain/opens-on clip)] inspected)
        [project-fields])]]))
